/* ---------------------------------------------------------------
   DISPLAY PREFERENCES (size / weight / density / corners)
   Each preference follows the same recipe as the theme + font
   pickers: a localStorage key, a CSS custom property written to
   document.documentElement, and a CustomEvent so any mounted UI
   stays in sync. Stored values are the resolved CSS values so the
   pre-paint bootstrap in index.html can apply them with no mapping.
   NOTE: index.html mirrors these storage keys — keep them in sync.
   ---------------------------------------------------------------*/

export const DISPLAY_EVENT = "displayPreferenceChange";

export const FONT_SCALE_KEY = "app.fontScale";
export const LINE_HEIGHT_KEY = "app.lineHeight";
export const RADIUS_SCALE_KEY = "app.radiusScale";

export const STRINGS = {
  HERO_TITLE: "Fine-tune your display",
  HERO_SUBTITLE:
    "Text size, density and corners apply across the whole workspace.",
  RESET: "Reset to defaults",
  PREVIEW_TITLE: "Live preview",
  PREVIEW_HEADING: "Portfolio overview",
  PREVIEW_BODY:
    "The quick brown fox jumps over the lazy dog while reviewing 1,234 exposures.",
  PREVIEW_PRIMARY: "Primary action",
  PREVIEW_SECONDARY: "Secondary",
  ACTIVE_LABEL: "Active",
};

export const DISPLAY_SETTINGS = [
  {
    key: "fontScale",
    storageKey: FONT_SCALE_KEY,
    cssVar: "--app-font-scale",
    label: "Text size",
    description: "Scales all text across the workspace. Layout and spacing stay fixed.",
    defaultValue: "1",
    options: [
      { value: "0.9", label: "Small", hint: "90%" },
      { value: "1", label: "Default", hint: "100%" },
      { value: "1.1", label: "Large", hint: "110%" },
      { value: "1.2", label: "Extra large", hint: "120%" },
    ],
  },
  {
    key: "lineHeight",
    storageKey: LINE_HEIGHT_KEY,
    cssVar: "--app-line-height",
    label: "Density",
    description: "Vertical spacing of text rows, lists and menus.",
    defaultValue: "1.5",
    options: [
      { value: "1.35", label: "Compact" },
      { value: "1.5", label: "Comfortable" },
      { value: "1.7", label: "Spacious" },
    ],
  },
  {
    key: "radiusScale",
    storageKey: RADIUS_SCALE_KEY,
    cssVar: "--app-radius-scale",
    label: "Corners",
    description: "Roundness of cards, buttons, inputs and dialogs.",
    defaultValue: "1",
    options: [
      { value: "0", label: "Sharp" },
      { value: "1", label: "Rounded" },
      { value: "1.6", label: "Extra" },
    ],
  },
];

const findSetting = (key) =>
  DISPLAY_SETTINGS.find((setting) => setting.key === key);

export const isValidValue = (setting, value) =>
  Boolean(setting) && setting.options.some((option) => option.value === value);

export const getStoredValue = (setting) => {
  try {
    const value = localStorage.getItem(setting.storageKey);
    return isValidValue(setting, value) ? value : setting.defaultValue;
  } catch (error) {
    return setting.defaultValue;
  }
};

export const getStoredValues = () =>
  DISPLAY_SETTINGS.reduce((acc, setting) => {
    acc[setting.key] = getStoredValue(setting);
    return acc;
  }, {});

export const applyDisplaySetting = (key, value, options = {}) => {
  const { silent = false } = options;
  const setting = findSetting(key);
  if (
    !setting ||
    typeof document === "undefined" ||
    !document.documentElement
  ) {
    return undefined;
  }
  const next = isValidValue(setting, value) ? value : setting.defaultValue;
  document.documentElement.style.setProperty(setting.cssVar, next);
  try {
    localStorage.setItem(setting.storageKey, next);
  } catch (error) {
    // localStorage may be unavailable; fail silently
  }
  if (!silent && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(DISPLAY_EVENT, { detail: { key, value: next } })
    );
  }
  return next;
};

export const resetDisplaySettings = () => {
  DISPLAY_SETTINGS.forEach((setting) =>
    applyDisplaySetting(setting.key, setting.defaultValue)
  );
};

export const initDisplaySettings = () => {
  DISPLAY_SETTINGS.forEach((setting) =>
    applyDisplaySetting(setting.key, getStoredValue(setting), { silent: true })
  );
};
